import { createContext, useContext, useMemo } from "react";
import type { NotifyPrescriptionsParams } from "@screens/FeaAuth/hooks";
import { notifyPrescriptions } from "@screens/FeaAuth/hooks";
import { useMutation } from "@tanstack/react-query";

interface Value {
  notifyPrescription: (params: NotifyPrescriptionsParams) => Promise<void>;
  isNotifyingPrescription: boolean;
  isErrorOnNotifyingPrescription: boolean;
  errorOnNotifyingPrescription: Error | null;
}

const DocumentNotificationContext = createContext<Value | null>(null);

export const useDocumentNotificationContext = () => {
  const value = useContext(DocumentNotificationContext);

  if (!value) {
    throw new Error("DocumentNotificationContext: No value provided");
  }

  return value;
};

export const DocumentNotificationContextProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const {
    mutateAsync: notifyPrescription,
    isPending: isNotifyingPrescription,
    isError: isErrorOnNotifyingPrescription,
    error: errorOnNotifyingPrescription,
  } = useMutation({
    mutationFn: notifyPrescriptions,
    onSuccess: () => null,
    onError: () => null,
  });

  const value = useMemo(
    () => ({
      notifyPrescription,
      isNotifyingPrescription,
      isErrorOnNotifyingPrescription,
      errorOnNotifyingPrescription,
    }),
    [
      notifyPrescription,
      isNotifyingPrescription,
      isErrorOnNotifyingPrescription,
      errorOnNotifyingPrescription,
    ],
  );

  return (
    <DocumentNotificationContext.Provider value={value}>
      {children}
    </DocumentNotificationContext.Provider>
  );
};
